import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Pill, UtensilsCrossed, Plus, Trash2, Pencil, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ProUpsell from "@/components/ProUpsell";

const EMPTY_FORM = {
  kind: "treatment",
  name: "",
  dose: "",
  applied_date: new Date().toISOString().slice(0, 10),
  withdrawal_days: "",
  follow_up_days: "",
  notes: "",
};

export default function TreatmentsTab({ hiveId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upsell, setUpsell] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm(EMPTY_FORM); setEditingId(null); setOpen(true); };
  const openEdit = (t) => {
    setForm({
      kind: t.kind || "treatment",
      name: t.name || "",
      dose: t.dose || "",
      applied_date: (t.applied_date || "").slice(0, 10) || EMPTY_FORM.applied_date,
      withdrawal_days: t.withdrawal_days != null ? String(t.withdrawal_days) : "",
      follow_up_days: t.follow_up_days != null ? String(t.follow_up_days) : "",
      notes: t.notes || "",
    });
    setEditingId(t.id);
    setOpen(true);
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/treatments", { params: { hive_id: hiveId } });
      setItems(r.data);
    } catch (e) {
      if (e.response?.status === 402) setUpsell(true);
      else toast.error(e.response?.data?.detail || "Failed to load treatments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hiveId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.applied_date) {
      toast.message("Name and date are required");
      return;
    }
    setSaving(true);
    try {
      const body = {
        kind: form.kind,
        name: form.name.trim(),
        dose: form.dose.trim(),
        applied_date: form.applied_date,
        withdrawal_days: form.withdrawal_days ? Number(form.withdrawal_days) : null,
        follow_up_days: form.follow_up_days ? Number(form.follow_up_days) : null,
        notes: form.notes.trim(),
      };
      if (editingId) {
        await api.patch(`/treatments/${editingId}`, body);
        toast.success("Updated");
      } else {
        await api.post("/treatments", { hive_id: hiveId, ...body });
        toast.success(`${form.kind === "feeding" ? "Feeding" : "Treatment"} logged`);
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      setOpen(false);
      load();
    } catch (err) {
      if (err.response?.status === 402) setUpsell(true);
      else toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this entry? Any auto-scheduled follow-up will also be removed.")) return;
    try {
      await api.delete(`/treatments/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <div className="text-sm text-stone-500 py-6">Loading…</div>;
  if (upsell) {
    return (
      <div data-testid="treatments-upsell">
        <ProUpsell open={upsell} onOpenChange={setUpsell} feature="treatments" />
        <div className="text-center py-12 border border-dashed border-stone-300 rounded-2xl">
          <Sparkles className="w-6 h-6 text-amber-500 mx-auto mb-2" />
          <p className="text-stone-600 text-sm">Treatments & feeding tracker is a Pro feature.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="treatments-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-medium">Treatments & feedings</h3>
        <Button onClick={openNew} size="sm" data-testid="add-treatment-btn" className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Log entry
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-stone-300 rounded-2xl text-stone-500 text-sm">
          No treatments or feedings logged yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((t) => (
            <li
              key={t.id}
              className="border border-stone-200 rounded-xl p-4 flex items-start justify-between gap-3"
              data-testid={`treatment-${t.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${t.kind === "feeding" ? "bg-emerald-100 text-emerald-700" : "bg-violet-100 text-violet-700"}`}>
                  {t.kind === "feeding" ? <UtensilsCrossed className="w-4 h-4" /> : <Pill className="w-4 h-4" />}
                </div>
                <div>
                  <div className="font-medium text-stone-900">
                    {t.name}
                    {t.dose && <span className="text-stone-500 text-sm font-normal"> · {t.dose}</span>}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    Applied {safeFmt(t.applied_date)}
                    {t.withdrawal_until && (
                      <span className="ml-2 inline-flex items-center gap-1 text-orange-700 font-medium">
                        · No-harvest until {safeFmt(t.withdrawal_until)}
                      </span>
                    )}
                    {t.follow_up_event_id && (
                      <span className="ml-2 text-emerald-700">· Follow-up scheduled</span>
                    )}
                  </div>
                  {t.notes && <div className="text-sm text-stone-700 mt-1.5">{t.notes}</div>}
                </div>
              </div>
              <div className="flex items-center shrink-0">
                <Button variant="ghost" size="icon" onClick={() => openEdit(t)} className="text-stone-400 hover:text-stone-700" title="Edit" data-testid={`edit-treatment-${t.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => del(t.id)} className="text-stone-400 hover:text-red-600" title="Delete" data-testid={`delete-treatment-${t.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditingId(null); }}>
        <DialogContent data-testid="treatment-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit treatment or feeding" : "Log a treatment or feeding"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="treatment-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="treatment">Treatment</SelectItem>
                  <SelectItem value="feeding">Feeding</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                maxLength={200}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={form.kind === "feeding" ? "1:1 sugar syrup" : "Apivar"}
                data-testid="treatment-name-input"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Dose</Label>
                <Input
                  value={form.dose}
                  maxLength={200}
                  onChange={(e) => setForm({ ...form, dose: e.target.value })}
                  placeholder="2 strips"
                  data-testid="treatment-dose-input"
                />
              </div>
              <div>
                <Label>Applied date *</Label>
                <Input
                  type="date"
                  value={form.applied_date}
                  onChange={(e) => setForm({ ...form, applied_date: e.target.value })}
                  data-testid="treatment-date-input"
                  required
                />
              </div>
            </div>
            {form.kind === "treatment" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Honey withdrawal (days)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={365}
                    value={form.withdrawal_days}
                    onChange={(e) => setForm({ ...form, withdrawal_days: e.target.value })}
                    placeholder="56"
                    data-testid="treatment-withdrawal-input"
                  />
                </div>
                <div>
                  <Label>Follow-up (days)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={365}
                    value={form.follow_up_days}
                    onChange={(e) => setForm({ ...form, follow_up_days: e.target.value })}
                    placeholder="42"
                    data-testid="treatment-followup-input"
                  />
                </div>
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Textarea
                rows={2}
                maxLength={10000}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="treatment-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold" data-testid="treatment-submit-btn">
                {saving ? "Saving…" : editingId ? "Save changes" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function safeFmt(d) {
  if (!d) return "—";
  try { return format(parseISO(d), "MMM d, yyyy"); }
  catch { return d; }
}
